import { useEffect } from 'react';
import type { VenteDto } from '../api/types';
import { fmtEur, fmtN } from '../lib/format';
import { formulaText } from '../lib/commission';

interface Props {
  vente: VenteDto;
  highlight?: string | null;   // ex: "🎯 Objectif du mois atteint !"
  onNewSale: () => void;       // bouton "Nouvelle vente"
  onSeeSales: () => void;      // bouton "Voir mes ventes"
}

/**
 * Modal de confirmation "Unibet style" après l'enregistrement d'une vente.
 * Reproduit le succès-modal de la maquette : icône check teal, récap collecte
 * / commission / formule / score, 2 boutons d'action.
 */
export function SuccessModal({ vente, highlight, onNewSale, onSeeSales }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onNewSale();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onNewSale]);

  const isImmo = vente.prod === 'immo-vente' || vente.prod === 'immo-apport' || vente.prod === 'scpi';
  const isPP = vente.prod === 'pp';

  // Pour la PP, la collecte annuelle = mensualité × 12
  const displayedAmount = isPP ? vente.montant * 12 : vente.montant;
  const amountLabel = isImmo ? 'Prix TTC' : (isPP ? 'Collecte annuelle' : 'Collecte');

  const subline = `${vente.clientPrenom} ${vente.clientNom} · ${vente.prodLabel}`;
  const formula = formulaText({
    prod: vente.prod,
    statut: vente.statut,
    origine: vente.origine,
    frais: vente.frais ?? null,
    lodeomType: vente.lodeomType ?? null
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onNewSale}
      role="dialog" aria-modal="true"
      aria-labelledby="success-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        {/* Handle pour effet bottom-sheet */}
        <div className="hidden sm:block" />
        <div className="sm:hidden flex justify-center -mt-2 mb-3">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Check + titre */}
        <div className="text-center mb-1">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-bg mb-3">
            <svg viewBox="0 0 40 40" className="w-9 h-9">
              <circle cx="20" cy="20" r="18" fill="none" stroke="#21B3A3" strokeWidth="2" />
              <path d="M12 20.5 L17.5 26 L28 14.5" fill="none" stroke="#21B3A3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 id="success-title" className="text-lg font-demi text-ink">
            Vente enregistrée
          </h2>
          <p className="text-xs text-ink-sub mt-1">{subline}</p>
        </div>

        {/* Highlight optionnel (ex: objectif atteint) */}
        {highlight && (
          <div className="mt-4 bg-srx-bg border border-srx/30 rounded-lg px-3 py-2 text-center text-sm text-srx-dark font-demi">
            {highlight}
          </div>
        )}

        {/* Récap */}
        <div className="mt-4 bg-surface-muted rounded-xl px-4 py-3 space-y-2.5">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-ink-sub">{amountLabel}</span>
            <span className="text-sm font-demi text-ink">{fmtEur(displayedAmount)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-ink-sub">Commission</span>
            <span className="text-xl font-demi text-teal-dark">{fmtEur(vente.commission)}</span>
          </div>
          {formula && (
            <div className="text-[11px] text-ink-dim font-light text-center pt-1 border-t border-border">
              <span className="font-mono">{formula}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline border-t border-border pt-2">
            <span className="text-sm text-ink-sub">Score</span>
            <span className="text-base font-demi text-srx-dark">+{fmtN(vente.score)} pts</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onNewSale}
            className="flex-1 py-3 rounded-xl border border-border bg-white text-ink hover:bg-surface-muted text-sm transition-colors"
          >
            Nouvelle vente
          </button>
          <button
            type="button"
            onClick={onSeeSales}
            className="flex-1 py-3 rounded-xl bg-teal text-white hover:bg-teal-dark text-sm transition-colors font-demi"
          >
            Voir mes ventes
          </button>
        </div>
      </div>
    </div>
  );
}
